/**
 * Accordion Block - Frontend JavaScript
 *
 * Closes accordions by default on mobile devices (< 700px).
 * Keeps accordions open on desktop (>= 700px) based on open__closed field value.
 * Preserves user-opened accordions on mobile when scrolling.
 */

(function () {
	/**
	 * Check if device is mobile based on viewport width.
	 *
	 * @return {boolean} True if mobile, false otherwise.
	 */
	function isMobile() {
		return window.innerWidth < 700;
	}

	// Track accordions that were manually opened by the user.
	const userOpenedAccordions = new WeakSet();

	/**
	 * Track user interactions with accordions.
	 */
	function trackUserInteractions() {
		const selectors = [
			'.wp-block-acf-proc-accordion.pro-accordion-holder',
			'.pro-accordion-holder',
			'[class*="proc-accordion"]',
		];

		let accordionHolders = [];
		selectors.some((selector) => {
			accordionHolders = document.querySelectorAll(selector);
			return accordionHolders.length > 0;
		});

		if (!accordionHolders.length) {
			return;
		}

		accordionHolders.forEach((accordionHolder) => {
			const detailsElements = accordionHolder.querySelectorAll('details');

			detailsElements.forEach((details) => {
				// Remove any existing listeners to avoid duplicates.
				const summary = details.querySelector('summary');
				if (summary && !summary.hasAttribute('data-accordion-tracked')) {
					summary.setAttribute('data-accordion-tracked', 'true');
					
					// Track when user opens an accordion.
					details.addEventListener('toggle', function() {
						if (details.open) {
							userOpenedAccordions.add(details);
						}
					}, { passive: true });
				}
			});
		});
	}

	/**
	 * Close all accordions on mobile, keep open on desktop.
	 * Preserves accordions that were manually opened by the user.
	 */
	function updateAccordionStates() {
		// Try multiple selectors to find accordion holders.
		const selectors = [
			'.wp-block-acf-proc-accordion.pro-accordion-holder',
			'.pro-accordion-holder',
			'[class*="proc-accordion"]',
		];

		let accordionHolders = [];
		selectors.some((selector) => {
			accordionHolders = document.querySelectorAll(selector);
			return accordionHolders.length > 0;
		});

		if (!accordionHolders.length) {
			return;
		}

		const isMobileDevice = isMobile();

		accordionHolders.forEach((accordionHolder) => {
			const detailsElements = accordionHolder.querySelectorAll('details');

			detailsElements.forEach((details) => {
				if (isMobileDevice) {
					// On mobile: only close accordions that weren't manually opened by the user.
					if (!userOpenedAccordions.has(details)) {
						details.removeAttribute('open');
					}
				}
				// On desktop: PHP sets the open attribute based on open__closed field,
				// so we don't need to modify it.
			});
		});
	}

	/**
	 * Initialize accordion mobile behavior.
	 */
	function init() {
		// Track user interactions first.
		trackUserInteractions();

		// Update accordion states immediately.
		updateAccordionStates();

		// Handle resize events to update accordion states.
		let resizeTimer;
		window.addEventListener(
			'resize',
			() => {
				clearTimeout(resizeTimer);
				resizeTimer = setTimeout(() => {
					trackUserInteractions();
					updateAccordionStates();
				}, 250);
			},
			{ passive: true },
		);

		// Watch for dynamically added accordion blocks.
		// Use a more targeted approach to avoid closing user-opened accordions.
		const observer = new MutationObserver((mutations) => {
			// Only update if new accordion blocks are added, not for all DOM changes.
			let shouldUpdate = false;
			for (const mutation of mutations) {
				for (const node of mutation.addedNodes) {
					if (node.nodeType === Node.ELEMENT_NODE) {
						// Check if the added node is or contains an accordion holder.
						if (
							node.matches?.('.pro-accordion-holder, [class*="proc-accordion"]') ||
							node.querySelector?.('.pro-accordion-holder, [class*="proc-accordion"]')
						) {
							shouldUpdate = true;
							break;
						}
					}
				}
				if (shouldUpdate) break;
			}

			if (shouldUpdate) {
				trackUserInteractions();
				updateAccordionStates();
			}
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});
	}

	// Run immediately if DOM is already loaded, otherwise wait for DOMContentLoaded.
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		// DOM already loaded, initialize immediately.
		init();
	}

	// Also initialize on embarkReady event (if blocks are loaded dynamically).
	document.addEventListener('embarkReady', init);

	// Run once more after a short delay to catch any late-loading content.
	setTimeout(() => {
		trackUserInteractions();
		updateAccordionStates();
	}, 100);
	setTimeout(() => {
		trackUserInteractions();
		updateAccordionStates();
	}, 500);
}());
